import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'producer' | 'customer';
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { user, profile, loading } = useAuth();
  const location = useLocation();
  const [cypressWaitComplete, setCypressWaitComplete] = React.useState(false);

  // Check if we're in Cypress test environment
  const isCypress = typeof window !== 'undefined' && (window as any).Cypress;

  // In Cypress, give Supabase a brief moment to restore session from localStorage
  React.useEffect(() => {
    if (isCypress && !user && !loading) {
      const hasSession = typeof window !== 'undefined' &&
        Object.keys(window.localStorage).some(key =>
          key.startsWith('sb-') && key.includes('-auth-token')
        );

      if (hasSession && !cypressWaitComplete) {
        // Wait 1500ms for Supabase to restore session, then allow redirect if still no user
        const timer = setTimeout(() => {
          setCypressWaitComplete(true);
        }, 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [isCypress, user, loading, cypressWaitComplete]);

  // Show loading state while checking authentication
  if (loading) {
    return <div className="p-4 text-center">Laddar...</div>;
  }

  // In Cypress, wait briefly if session exists in localStorage but user not yet set
  if (isCypress && !user && !cypressWaitComplete && typeof window !== 'undefined') {
    const hasSession = Object.keys(window.localStorage).some(key =>
      key.startsWith('sb-') && key.includes('-auth-token')
    );
    if (hasSession) {
      return <div className="p-4 text-center">Laddar...</div>;
    }
  }

  // If not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // If a specific role is required, check it
  if (requiredRole && profile?.role !== requiredRole) {
    return <Navigate to="/" replace />;
  }

  // User is authenticated and has the required role
  return <>{children}</>;
} 